// Web client wrappers around the platform /api/ticketing endpoints.
// publicRequest for browse (guest-viewable); userRequest for anything tied to
// the signed-in customer (holds, checkout, tickets).
import { publicRequest, userRequest } from "./requestMethods";
import { getTicketDeviceId, holdsTicket } from "../utils/liveTicketStore";

// Names this browser to the server, which binds live tickets to one device
// (src/utils/liveTicketStore.js). Sent only for a ticket this browser already
// holds, or when the customer moves one here.
const deviceHeaders = () => ({ headers: { "x-ticket-device": getTicketDeviceId() } });

export const apiGetTicketingConfig = () =>
  publicRequest.get("/api/ticketing/config").then((r) => r.data);

export const apiGetEvents = (params = {}) =>
  publicRequest.get("/api/ticketing/events", { params }).then((r) => r.data);

export const apiGetEvent = (id) =>
  publicRequest.get(`/api/ticketing/events/${id}`).then((r) => r.data);

export const apiGetShowing = (id) =>
  publicRequest.get(`/api/ticketing/showings/${id}`).then((r) => r.data);

export const apiCreateHold = (body) =>
  userRequest.post("/api/ticketing/holds", body).then((r) => r.data);

export const apiReleaseHold = (id) =>
  userRequest.delete(`/api/ticketing/holds/${id}`).then((r) => r.data);

export const apiCheckout = (body) =>
  userRequest.post("/api/ticketing/checkout", body).then((r) => r.data);

export const apiConfirmTicketOrder = (orderId) =>
  userRequest.post(`/api/ticketing/orders/${orderId}/confirm`).then((r) => r.data);

/**
 * GET /api/ticketing/my-holds — seats reserved but not yet paid for.
 *
 * The endpoint has existed since ticketing shipped; the WEB client simply never
 * called it. So a web customer whose payment failed saw "You have no tickets
 * yet" while their seats were still being held for them — no way to retry, and
 * no idea the reservation existed at all until it silently expired and the
 * seats went back on sale. Mobile has listed these all along.
 */
export const apiGetMyHolds = () =>
  userRequest.get("/api/ticketing/my-holds").then((r) => r.data);

export const apiGetMyTickets = () =>
  userRequest.get("/api/ticketing/my-tickets").then((r) => r.data);

/** A ticket, with its live key when this browser holds it. */
export const apiGetTicket = (id) =>
  userRequest
    .get(`/api/ticketing/tickets/${id}`, holdsTicket(id) ? deviceHeaders() : undefined)
    .then((r) => r.data);

/** POST /tickets/:id/move-here: show the ticket on this device instead. */
export const apiMoveTicketHere = (id) =>
  userRequest.post(`/api/ticketing/tickets/${id}/move-here`, {}, deviceHeaders()).then((r) => r.data);

export const apiRequestTicketRefund = (ticketId, reason) =>
  userRequest.post(`/api/ticketing/tickets/${ticketId}/refund-request`, { reason }).then((r) => r.data);
